/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */





 
/*
 document.addEventListener('DOMContentLoaded', function () {
  const FREE_PRODUCT_HANDLE = 'n101-subscribers-only-free-product';
  const REQUIRED_DISCOUNT = 'VIPGIFT';
  const MIN_SUBTOTAL = 10100; // $101 in cents

  // -------------------------------------------------------------------
  // Detect use of your VIPGIFT link via ?vipgift=1 on the product URL
  // (link: /discount/VIPGIFT?redirect=/products/... ?vipgift=1)
  // -------------------------------------------------------------------
  (function detectVipGiftLink() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('vipgift') === '1') {
        sessionStorage.setItem('vipgift_link_used', 'true');
      }
    } catch (e) {
      console.warn('VIPGIFT link detection failed:', e);
    }
  })();

  function vipGiftLinkUsed() {
    return sessionStorage.getItem('vipgift_link_used') === 'true';
  }

  // -------------------------------------------------------------------
  // Helpers to work with the cart
  // -------------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function removeLine(line) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ line: line, quantity: 0 })
    });
  }

  // -------------------------------------------------------------------
  // Main enforcement logic
  // -------------------------------------------------------------------
  function enforceFreeProductRules(lastAddedVariantId) {
    getCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        const discountFromCart =
          cart.discount_code && cart.discount_code.code === REQUIRED_DISCOUNT;

        // Discount is valid if actually applied OR VIP link used this session
        const discountIsValid = discountFromCart || vipGiftLinkUsed();

        const subtotalOk = cart.items_subtotal_price >= MIN_SUBTOTAL;

        // Find all lines that are the free product
        const freeProductLines = cart.items
          .map(function (item, index) {
            return { item: item, line: index + 1 }; // cart line is 1-based
          })
          .filter(function (entry) {
            return entry.item.handle === FREE_PRODUCT_HANDLE;
          });

        const hasFreeProduct = freeProductLines.length > 0;

        // 1) If free product is in cart but requirements are not met => remove it
        if (hasFreeProduct && (!discountIsValid || !subtotalOk)) {
          const removals = freeProductLines.map(function (entry) {
            return removeLine(entry.line);
          });

          Promise.all(removals)
            .then(function () {
              if (!subtotalOk) {
                alert(
                  'Your cart subtotal must be at least $101 before you can add this item.'
                );
              } else if (!discountIsValid) {
                alert(
                  'The VIPGIFT offer must be activated using the VIPGIFT link before you can add this item to your cart.'
                );
              }

              // Hard reload so cart page & drawer both reflect the change
              window.location.reload();
            })
            .catch(function (err) {
              console.error(
                'Error removing free product when requirements not met:',
                err
              );
            });

          return;
        }

        // 2) Prevent multiple variants of the free product
        if (freeProductLines.length > 1) {
          let lineToRemove;

          if (lastAddedVariantId) {
            lineToRemove = freeProductLines.find(function (entry) {
              return entry.item.id === lastAddedVariantId;
            });
          }

          if (!lineToRemove) {
            // fallback: just remove the last one
            lineToRemove = freeProductLines[freeProductLines.length - 1];
          }

          removeLine(lineToRemove.line)
            .then(function () {
              alert('You can only add ONE free gift product.');
              window.location.reload();
            })
            .catch(function (err) {
              console.error('Error removing extra free product variant:', err);
            });

          return;
        }

        // 3) Edge case: user JUST added free product but requirements aren’t met
        if (lastAddedVariantId) {
          const justAdded = cart.items.find(function (item) {
            return item.id === lastAddedVariantId;
          });

          if (justAdded && justAdded.handle === FREE_PRODUCT_HANDLE) {
            if (!discountIsValid || !subtotalOk) {
              const lineIndex =
                cart.items.findIndex(function (item) {
                  return item.id === lastAddedVariantId;
                }) + 1;

              removeLine(lineIndex)
                .then(function () {
                  if (!subtotalOk) {
                    alert(
                      'Your cart subtotal must be at least $101 before you can add the free product.'
                    );
                  } else {
                    alert(
                      'You must use the VIPGIFT link before adding the free product.'
                    );
                  }
                  window.location.reload();
                })
                .catch(function (err) {
                  console.error(
                    'Error handling free product add when invalid:',
                    err
                  );
                });
            }
          }
        }
      })
      .catch(function (err) {
        console.error('Error in enforceFreeProductRules:', err);
      });
  }

  // -------------------------------------------------------------------
  // 1) Hook into Symmetry’s AJAX add-to-cart event (drawer)
  // -------------------------------------------------------------------
  document.addEventListener('on:cart:add', function (event) {
    const variantIdAdded = event.detail && event.detail.variantId;
    // Give Symmetry a moment to update the cart, then enforce
    setTimeout(function () {
      enforceFreeProductRules(variantIdAdded);
    }, 400);
  });

  // -------------------------------------------------------------------
  // 2) Enforce once on any page load
  // -------------------------------------------------------------------
  enforceFreeProductRules();

  // -------------------------------------------------------------------
  // 3) EXTRA: Poll the cart while on the cart page
  //     This guarantees removal when subtotal dips below $101
  // -------------------------------------------------------------------
  if (window.location.pathname.startsWith('/cart')) {
    setInterval(function () {
      enforceFreeProductRules();
    }, 1500); // every 1.5 seconds
  }
});
*/



document.addEventListener('DOMContentLoaded', function () {
  function updateSpecialOrderSkuMessage() {
    var skuWrapper = document.getElementById('product-sku');

    if (!skuWrapper) return;

    var skuValue = skuWrapper.querySelector('.product-sku__value');
    var specialOrderMessage = skuWrapper.querySelector('.product-special-order-message');

    if (!skuValue || !specialOrderMessage) return;

    var skuText = skuValue.textContent || '';
    var isSpecialOrder = skuText.toLowerCase().indexOf('special order') !== -1;

    if (isSpecialOrder) {
      skuWrapper.classList.add('product-sku--is-special-order');
      specialOrderMessage.hidden = false;
    } else {
      skuWrapper.classList.remove('product-sku--is-special-order');
      specialOrderMessage.hidden = true;
    }
  }

  updateSpecialOrderSkuMessage();

  var skuValue = document.querySelector('#product-sku .product-sku__value');

  if (skuValue) {
    var skuObserver = new MutationObserver(function () {
      updateSpecialOrderSkuMessage();
    });

    skuObserver.observe(skuValue, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  document.addEventListener('change', function (event) {
    if (
      event.target.matches('select') ||
      event.target.matches('input[type="radio"]')
    ) {
      setTimeout(updateSpecialOrderSkuMessage, 100);
    }
  });

  document.addEventListener('variant:change', function () {
    setTimeout(updateSpecialOrderSkuMessage, 100);
  });
});

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.cnv-accordion').forEach(function (accordion) {
    const buttons = accordion.querySelectorAll('.cnv-toggle-all');
    const details = accordion.querySelectorAll('details');

    if (!buttons.length || !details.length) return;

    function updateButtons() {
      const openCount = accordion.querySelectorAll('details[open]').length;
      const shouldCollapse = openCount > details.length / 2;

      buttons.forEach(function (button) {
        button.textContent = shouldCollapse ? 'Collapse All' : 'Expand All';
        button.setAttribute('aria-expanded', shouldCollapse ? 'true' : 'false');
      });
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        const shouldExpand = button.textContent.trim() === 'Expand All';

        details.forEach(function (detail) {
          detail.open = shouldExpand;
        });

        updateButtons();

        if (!shouldExpand && button.classList.contains('cnv-toggle-all-bottom')) {
          accordion.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });

    details.forEach(function (detail) {
      detail.addEventListener('toggle', updateButtons);
    });

    updateButtons();
  });
});